import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import dayjs from 'dayjs';

/**
 * Configuration for validating a FormArray entry
 */
export interface FormArrayEntryConfig {
	/** Array of field keys to check for emptiness */
	fields: string[];
	/** Name of the FormArray for error messages (e.g., "Emergency Contact", "Additional Debt") */
	displayName: string;
	/** Custom message when entry is empty. If not provided, uses default message */
	emptyMessage?: string;
}

/**
 * Function to get field label from Formly field config
 */
export type GetFieldLabelFn = (fields: FormlyFieldConfig[], key: string) => string;

/**
 * Configuration for field-specific validation
 */
export interface FieldValidationConfig {
	/** Field key to check */
	fieldKey: string;
	/** Condition to check (returns true if validation should be applied) */
	condition: (group: AbstractControl) => boolean;
	/** Error message to display */
	errorMessage: (index: number) => string;
	/** Error type to check (default: 'required') */
	errorType?: string;
}

/**
 * Checks if a FormArray entry is completely empty based on the provided field list
 *
 * @param group - The FormGroup to check
 * @param fields - Array of field keys to check
 * @returns true if all specified fields are empty, false otherwise
 */
export function isFormArrayEntryEmpty(
	group: AbstractControl,
	fields: string[]
): boolean {
	return fields.every((fieldKey) => {
		const control = group.get(fieldKey);
		const value = control?.value;

		// Check for null, undefined, empty string, or empty array
		if (value === null || value === undefined || value === '') {
			return true;
		}

		// Check for empty arrays
		if (Array.isArray(value) && value.length === 0) {
			return true;
		}

		// Check for empty objects (excluding dayjs dates and other valid objects)
		if (
			typeof value === 'object' &&
			!dayjs.isDayjs(value) &&
			Object.keys(value).length === 0
		) {
			return true;
		}

		return false;
	});
}

/**
 * Validates FormArray entries for empty records and adds appropriate errors
 *
 * @param formArray - The FormArray to validate
 * @param config - Configuration for validation
 * @param validationErrors - Array to collect validation error messages
 */
export function validateFormArrayEntries(
	formArray: FormArray,
	config: FormArrayEntryConfig,
	validationErrors: string[]
): void {
	formArray.controls.forEach((group, index) => {
		const isEmpty = isFormArrayEntryEmpty(group, config.fields);

		if (isEmpty) {
			const errorMessage =
				config.emptyMessage ||
				`${config.displayName} ${index + 1}: Please fill in at least one field or remove this entry`;

			validationErrors.push(errorMessage);
			// Mark the form as invalid
			group.setErrors({ emptyEntry: true });
		} else {
			// Clear the empty entry error if there's data
			if (group.errors?.['emptyEntry']) {
				const errors = { ...group.errors };
				delete errors['emptyEntry'];
				group.setErrors(Object.keys(errors).length > 0 ? errors : null);
			}
		}
	});
}

/**
 * Validates specific fields in FormArray entries based on conditions
 *
 * @param formArray - The FormArray to validate
 * @param displayName - Name for error messages (e.g., "Emergency Contact")
 * @param fieldConfigs - Array of field validation configurations
 * @param validationErrors - Array to collect validation error messages
 */
export function validateFormArrayFields(
	formArray: FormArray,
	displayName: string,
	fieldConfigs: FieldValidationConfig[],
	validationErrors: string[]
): void {
	formArray.controls.forEach((group, index) => {
		// Skip entries that are marked as empty
		if (group.errors?.['emptyEntry']) {
			return;
		}

		if (group.invalid) {
			fieldConfigs.forEach((config) => {
				const control = group.get(config.fieldKey);
				const errorType = config.errorType || 'required';

				if (config.condition(group) && control?.errors?.[errorType]) {
					validationErrors.push(config.errorMessage(index + 1));
				}
			});
		}
	});
}

/**
 * Filters out empty entries from a FormArray data array
 *
 * @param data - Array of form data
 * @param fields - Array of field keys to check
 * @returns Filtered array with non-empty entries only
 */
export function filterEmptyFormArrayEntries<T>(
	data: T[],
	fields: string[]
): T[] {
	if (!data || !Array.isArray(data)) {
		return [];
	}

	return data.filter((entry: any) => {
		return fields.some((fieldKey) => {
			const value = entry[fieldKey];

			// Check if field has a meaningful value
			if (value === null || value === undefined || value === '') {
				return false;
			}

			// Check for non-empty arrays
			if (Array.isArray(value) && value.length === 0) {
				return false;
			}

			// Check for non-empty objects
			if (
				typeof value === 'object' &&
				!dayjs.isDayjs(value) &&
				Object.keys(value).length === 0
			) {
				return false;
			}

			return true;
		});
	});
}

/**
 * Convenience function to validate emergency contacts
 *
 * @param formArray - Speak on behalfs FormArray
 * @param validationErrors - Array to collect validation error messages
 */
export function validateSpeakOnBehalfs(
	formArray: FormArray,
	validationErrors: string[]
): void {
	// Validate required fields (empty row checking removed)
	validateFormArrayFields(
		formArray,
		'Speak on behalf',
		[
			{
				fieldKey: 'Applicant_Type',
				condition: () => true,
				errorMessage: (index) => `Speak On Behalf ${index}: Applicant Type is required`,
			},
			{
				fieldKey: 'Full_Name',
				condition: () => true,
				errorMessage: (index) =>
					`Speak On Behalf ${index}: Full Name is required`,
			},
			{
				fieldKey: 'Relation_to_Applicant',
				condition: () => true,
				errorMessage: (index) => `Speak On Behalf ${index}: Relation to Applicant is required`,
			},
		],
		validationErrors
	);
}

/**
 * Convenience function to validate emergency contacts
 *
 * @param formArray - Emergency contacts FormArray
 * @param validationErrors - Array to collect validation error messages
 */
export function validateEmergencyContacts(
	formArray: FormArray,
	validationErrors: string[]
): void {
	// Validate required fields (empty row checking removed)
	validateFormArrayFields(
		formArray,
		'Emergency Contact',
		[
			{
				fieldKey: 'Name1',
				condition: () => true,
				errorMessage: (index) => `Emergency Contact ${index}: Name is required`,
			},
			{
				fieldKey: 'Relation_to_Applicant',
				condition: () => true,
				errorMessage: (index) =>
					`Emergency Contact ${index}: Relationship is required`,
			},
			{
				fieldKey: 'Phone_Number',
				condition: () => true,
				errorMessage: (index) => `Emergency Contact ${index}: Phone is required`,
			},
		],
		validationErrors
	);
}

/**
 * Convenience function to validate co-applicant emergency contacts
 *
 * @param formArray - Co-applicant Emergency contacts FormArray
 * @param validationErrors - Array to collect validation error messages
 */
export function validateCoAppEmergencyContacts(
	formArray: FormArray,
	validationErrors: string[]
): void {
	// Validate required fields (empty row checking removed)
	validateFormArrayFields(
		formArray,
		'Emergency Contact',
		[
			{
				fieldKey: 'Name1',
				condition: () => true,
				errorMessage: (index) => `Co-applicant Emergency Contact ${index}: Name is required`,
			},
			{
				fieldKey: 'Relation_to_Applicant',
				condition: () => true,
				errorMessage: (index) =>
					`Co-applicant Emergency Contact ${index}: Relationship is required`,
			},
			{
				fieldKey: 'Phone_Number',
				condition: () => true,
				errorMessage: (index) => `Co-applicant Emergency Contact ${index}: Phone is required`,
			},
		],
		validationErrors
	);
}

/**
 * Convenience function to validate additional debts
 *
 * @param formArray - Additional debts FormArray
 * @param validationErrors - Array to collect validation error messages
 */
export function validateAdditionalDebts(
	formArray: FormArray,
	validationErrors: string[]
): void {
	// Validate conditional required fields (empty row checking removed)
	validateFormArrayFields(
		formArray,
		'Additional Debt',
		[
			{
				fieldKey: 'misc',
				condition: () => true,
				errorMessage: (index) =>
					`Additional Debt ${index}: Creditor is required`,
			},
			{
				fieldKey: 'Custom_Settlement',
				condition: () => true,
				errorMessage: (index) =>
					`Additional Debt ${index}: Custom Settlement is required`,
			},
			{
				fieldKey: 'Account_Balance',
				condition: (group) => !!group.get('Creditor_Portfolio')?.value,
				errorMessage: (index) =>
					`Additional Debt ${index}: Account Balance is required when Creditor Portfolio is selected`,
			},
			{
				fieldKey: 'Custom_Agency_Amount',
				condition: (group) => group.get('Custom_Settlement')?.value === 'Yes',
				errorMessage: (index) =>
					`Additional Debt ${index}: Custom Agency Amount is required when Custom Settlement is Yes`,
			},
		],
		validationErrors
	);
}

/**
 * Validates regular form controls (non-FormArray fields) and collects error messages
 *
 * @param form - FormGroup to validate
 * @param validationErrors - Array to collect validation error messages
 * @param getFieldLabel - Optional function to get field label (for better error messages)
 */
export function validateFormControls(
	form: FormGroup,
	validationErrors: string[],
	getFieldLabel?: GetFieldLabelFn
): void {
	Object.keys(form.controls).forEach((key) => {
		const control = form.controls[key];

		// Skip FormArrays as they should be handled separately
		if (control instanceof FormArray) {
			return;
		}

		if (control?.invalid && control.errors) {
			// Get field label if function provided, otherwise use the key
			const label = getFieldLabel
				? getFieldLabel([] as FormlyFieldConfig[], key)
				: key;

			// Check for specific error types
			if (control.errors['required']) {
				validationErrors.push(`${label} is required`);
			} else if (control.errors['email']) {
				validationErrors.push(`${label} must be a valid email address`);
			} else if (control.errors['namePattern']) {
				validationErrors.push(
					`${label}: ${control.errors['namePattern'].message || 'Invalid name format'}`
				);
			} else if (control.errors['pattern']) {
				validationErrors.push(`${label} has invalid format`);
			} else if (control.errors['minlength']) {
				const minLength = control.errors['minlength'].requiredLength;
				validationErrors.push(`${label} must be at least ${minLength} characters`);
			} else if (control.errors['maxlength']) {
				const maxLength = control.errors['maxlength'].requiredLength;
				validationErrors.push(`${label} must not exceed ${maxLength} characters`);
			} else if (control.errors['min']) {
				const min = control.errors['min'].min;
				validationErrors.push(`${label} must be at least ${min}`);
			} else if (control.errors['max']) {
				const max = control.errors['max'].max;
				validationErrors.push(`${label} must not exceed ${max}`);
			} else {
				// Generic error message for unknown error types
				validationErrors.push(`${label} is invalid`);
			}
		}
	});
}